import type {
  AdminChatThread,
  AdminCompanyInput,
  AdminCompanyPatch,
  AdminCompanyRow,
  AdminDictionaryItem,
  AdminDictionaryPatch,
  AdminLotBid,
  AdminUserInput,
  AdminUserPatch,
  AdminUserRow,
  ChatMessage,
  CompanyOption,
  CurrentUser,
  InviteLink,
  LotChat,
  LotDetail,
  LotDetailLot,
  LotInput,
  LotListItem,
  LotWinner,
  NotificationsResponse,
  PaymentTask,
  PaymentTaskRow,
  ProjectOption,
  VehicleTypeOption,
  WinnerDocStage,
  WinnerDocType,
  WinnerPatch,
} from "@omts/shared";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

/** Ошибки валидации сервера (zod flatten()): по полям и общие. */
export type ValidationDetails = { formErrors?: string[]; fieldErrors?: Record<string, string[] | undefined> };

export function fieldErrorsOf(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError) || err.status !== 400 || !err.details || typeof err.details !== "object") return {};
  const fe = (err.details as ValidationDetails).fieldErrors ?? {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(fe)) if (v && v.length) out[k] = v.join("; ");
  return out;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", ...(init.headers ?? {}) },
    ...init,
  });
  if (res.status === 204) return undefined as T;
  const body = (await res.json().catch(() => ({}))) as { error?: string; details?: unknown };
  if (!res.ok) throw new ApiError(res.status, body.error ?? res.statusText, body.details);
  return body as T;
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });
const patch = <T>(path: string, body: unknown) => request<T>(path, { method: "PATCH", body: JSON.stringify(body) });

/** Тело POST /api/lots: даты уходят ISO-строками, zod на сервере приводит их к Date. */
export type LotInputBody = Omit<LotInput, "loadingAt" | "deadlineAt"> & { loadingAt: string; deadlineAt: string };

export const api = {
  login: (email: string, password: string) => post<CurrentUser>("/api/auth/login", { email, password }),
  logout: () => post<void>("/api/auth/logout"),
  me: () => request<CurrentUser>("/api/auth/me"),
  lots: (includeClosed: boolean) => request<LotListItem[]>(`/api/lots?includeClosed=${includeClosed ? 1 : 0}`),
  lot: (id: string) => request<LotDetail>(`/api/lots/${id}`),
  createLot: (input: LotInputBody) => post<LotDetailLot>("/api/lots", input),
  updateLot: (id: string, input: LotInputBody) =>
    request<LotDetailLot>(`/api/lots/${id}`, { method: "PUT", body: JSON.stringify(input) }),
  publishLot: (id: string) => post<LotDetailLot>(`/api/lots/${id}/publish`),
  extendLot: (id: string, deadlineAt: string, reason: string) => post<LotDetailLot>(`/api/lots/${id}/extend`, { deadlineAt, reason }),
  finishLotEarly: (id: string) => post<unknown>(`/api/lots/${id}/finish-early`),
  cancelLot: (id: string, reason: string) => post<LotDetailLot>(`/api/lots/${id}/cancel`, { reason }),
  /** Файл уходит как тело запроса; имя, тип и этап документа — в заголовках (см. lots.ts). */
  uploadWinnerDocument: async (id: string, docType: WinnerDocType, file: File, stage: WinnerDocStage = "pre_trip") => {
    const res = await fetch(`/api/lots/${id}/winner/documents`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
        "X-File-Name": encodeURIComponent(file.name),
        "X-Doc-Type": docType,
        "X-Doc-Stage": stage,
      },
      body: file,
    });
    const body = (await res.json().catch(() => ({}))) as { error?: string; details?: unknown };
    if (!res.ok) throw new ApiError(res.status, body.error ?? res.statusText, body.details);
    return body as unknown as LotWinner;
  },
  deleteWinnerDocument: (id: string, docId: string) => request<LotWinner>(`/api/lots/${id}/winner/documents/${docId}`, { method: "DELETE" }),
  fileUrl: (fileId: string, download = false) => `/api/files/${fileId}${download ? "?download=1" : ""}`,
  updateWinner: (id: string, patch: WinnerPatch) =>
    request<LotWinner>(`/api/lots/${id}/winner`, { method: "PATCH", body: JSON.stringify(patch) }),
  // Этап «Доставка и оплата» (раздел 14).
  confirmDelivery: (id: string) => post<{ lot: LotDetailLot; taskId: string }>(`/api/lots/${id}/confirm-delivery`),
  requestDeliveryFix: (id: string, text: string) => post<LotDetailLot>(`/api/lots/${id}/request-delivery-fix`, { text }),
  /** Ссылка на PDF «Договор-заявка» — скачивается браузером с cookie-сессией. */
  paymentRequestPdfUrl: (id: string) => `/api/lots/${id}/payment-request.pdf`,
  paymentTasks: (status: "open" | "paid", period?: { from?: string; to?: string }) => {
    const q = new URLSearchParams({ status });
    if (period?.from) q.set("from", period.from);
    if (period?.to) q.set("to", period.to);
    return request<PaymentTaskRow[]>(`/api/payment-tasks?${q.toString()}`);
  },
  markPaid: (taskId: string) => post<PaymentTask>(`/api/payment-tasks/${taskId}/paid`),
  // Чат «перевозчик ↔ логист» (раздел 15.2).
  lotChat: (id: string) => request<LotChat>(`/api/lots/${id}/chat`),
  sendChat: (id: string, companyId: string, text: string) =>
    post<{ message: ChatMessage; delivered: boolean }>(`/api/lots/${id}/chat/${companyId}`, { text }),
  readChat: (id: string, companyId: string) => post<void>(`/api/lots/${id}/chat/${companyId}/read`),
  // Уведомления (раздел 15.1).
  notifications: (limit = 20) => request<NotificationsResponse>(`/api/me/notifications?limit=${limit}`),
  readNotification: (id: string) => post<void>(`/api/me/notifications/${id}/read`),
  readAllNotifications: () => post<void>("/api/me/notifications/read-all"),
  projects: () => request<ProjectOption[]>("/api/dictionaries/projects"),
  vehicleTypes: () => request<VehicleTypeOption[]>("/api/dictionaries/vehicle-types"),
  companies: () => request<CompanyOption[]>("/api/dictionaries/companies"),
  telegramInvite: () => post<InviteLink>("/api/me/telegram-invite"),
};

/** Админ-панель (/api/admin/*, только роль admin). */
export const adminApi = {
  lotBids: (lotId: string) => request<AdminLotBid[]>(`/api/admin/lots/${lotId}/bids`),
  chats: () => request<AdminChatThread[]>("/api/admin/chats"),
  companies: () => request<AdminCompanyRow[]>("/api/admin/companies"),
  createCompany: (input: AdminCompanyInput) => post<AdminCompanyRow>("/api/admin/companies", input),
  updateCompany: (id: string, input: AdminCompanyPatch) => patch<AdminCompanyRow>(`/api/admin/companies/${id}`, input),
  contactInvite: (contactId: string) => post<InviteLink>(`/api/admin/contacts/${contactId}/invite`),
  contactUnlink: (contactId: string) => post<void>(`/api/admin/contacts/${contactId}/unlink`),
  users: () => request<AdminUserRow[]>("/api/admin/users"),
  createUser: (input: AdminUserInput) => post<AdminUserRow>("/api/admin/users", input),
  updateUser: (id: string, input: AdminUserPatch) => patch<AdminUserRow>(`/api/admin/users/${id}`, input),
  userUnlinkTelegram: (id: string) => post<void>(`/api/admin/users/${id}/unlink-telegram`),
  projects: () => request<AdminDictionaryItem[]>("/api/admin/projects"),
  createProject: (name: string) => post<AdminDictionaryItem>("/api/admin/projects", { name }),
  updateProject: (id: string, input: AdminDictionaryPatch) => patch<AdminDictionaryItem>(`/api/admin/projects/${id}`, input),
  vehicleTypes: () => request<AdminDictionaryItem[]>("/api/admin/vehicle-types"),
  createVehicleType: (name: string) => post<AdminDictionaryItem>("/api/admin/vehicle-types", { name }),
  updateVehicleType: (id: string, input: AdminDictionaryPatch) =>
    patch<AdminDictionaryItem>(`/api/admin/vehicle-types/${id}`, input),
};

/** Типы событий SSE (см. services/realtime.ts на сервере). */
export type RealtimeEventType = "lot.updated" | "notification" | "chat.message" | "chat.read";
const EVENT_TYPES: RealtimeEventType[] = ["lot.updated", "notification", "chat.message", "chat.read"];

type EventListener = (type: RealtimeEventType, data: unknown) => void;
let eventSource: EventSource | null = null;
const listeners = new Set<EventListener>();

/**
 * Подписка на SSE: вызывает onEvent при каждом событии сервера.
 * Одно соединение на вкладку браузера — его делят колокольчик, список и карточка; закрывается с последним подписчиком.
 */
export function subscribeEvents(onEvent: EventListener): () => void {
  listeners.add(onEvent);
  if (!eventSource) {
    const es = new EventSource("/api/events");
    for (const type of EVENT_TYPES) {
      es.addEventListener(type, (e: MessageEvent) => {
        const data: unknown = JSON.parse(e.data as string);
        for (const l of listeners) l(type, data);
      });
    }
    eventSource = es;
  }
  return () => {
    listeners.delete(onEvent);
    if (listeners.size === 0 && eventSource) {
      eventSource.close();
      eventSource = null;
    }
  };
}
